"use client";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { GetChildrenInformation } from "@/actions/templates/ChildrenInformation/GetChildrenInformation";
import HistorySection from "@/components/dashboard/history/HistorySection";
import HistoryRecordsTable from "@/components/dashboard/HistoryRecordsTable";
import {
  EVENT_LIBRARY,
  HISTORY_TEMPLATES,
  REQUIRED_FIELD,
} from "@/lib/constants";
import {
  ChildrenInformationPayload,
  PostChildrenInformation,
} from "@/actions/templates/ChildrenInformation/PostChildrenInformation";
import { PutChildrenInformation } from "@/actions/templates/ChildrenInformation/PutChildrenInformation";
import { useForm } from "react-hook-form";
import { DeleteChildrenInformation } from "@/actions/templates/ChildrenInformation/DeleteChildrenInformation";
import HistoryModal from "@/components/dashboard/history/HistoryModal";
import FormRow from "@/components/dashboard/application/FormRow";
import { AuthContext } from "@/providers/auth";
import useAnalytics from "@/lib/hooks/useAnalytics";
import handleServerAction from "@/lib/hooks/handleServerAction";
import { useIsTextMasked } from "./useIsDataMasked";
import { InputWrapper } from "@/components/forms/JailTextInputWrapper";

const category = "Children";
const defaultValues = HISTORY_TEMPLATES[category].defaultValues;

const ChildrenHistoryPage = () => {
  const { getUserDetails } = useContext(AuthContext);
  const toast = useToast();
  const track = useAnalytics();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<any[]>([]);

  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ChildrenInformationPayload>({
    defaultValues: defaultValues,
  });

  const getRecords = async () => {
    setIsLoading(true);
    const data = await handleServerAction(GetChildrenInformation);
    setRecords(data);
    setIsLoading(false);
  };

  useEffect(() => {
    getRecords();
  }, []);

  const refreshState = async () => {
    onClose();
    await getRecords();
    await getUserDetails();
    reset(defaultValues);
  };

  const onEdit = (id: number) => {
    const editRecord = records.find((record) => record.id === id);
    if (editRecord) {
      reset({
        Id: editRecord?.id,
        FirstName: editRecord?.firstName,
        MiddleName: editRecord?.middleName,
        LastName: editRecord?.lastName,
        IsCurrentlyMarried: editRecord?.isCurrentlyMarried,
        IsDisabled: editRecord?.isDisabled,
      });
      onOpen();
    }
  };

  const onAdd = async (payload: ChildrenInformationPayload) => {
    try {
      await handleServerAction(PostChildrenInformation, payload);
      await refreshState();
      toast({
        status: "success",
        title: "Record Added",
        description: "Record has been successfully added.",
      });

      track(EVENT_LIBRARY.ADDED_HISTORY, {
        category,
      });
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  const onUpdate = async (payload: ChildrenInformationPayload) => {
    try {
      await handleServerAction(PutChildrenInformation, payload);
      await refreshState();
      toast({
        status: "success",
        title: "Record Updated",
        description: "Record has been successfully updated.",
      });

      track(EVENT_LIBRARY.UPDATED_HISTORY, {
        category,
      });
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  const onClick = () => {
    reset(defaultValues);
    onOpen();
  };

  const Id = watch("Id");
  const isTextMasked = useIsTextMasked() && Id;

  return (
    <HistorySection category={category} isLoading={isLoading} onClick={onClick}>
      <HistoryRecordsTable
        category={category}
        records={records}
        cells={records.map((record) => [record.firstName, record.lastName])}
        onEdit={onEdit}
        deleteAction={DeleteChildrenInformation}
        getRecords={getRecords}
      />
      <HistoryModal
        category={category}
        mode={Id ? "Update" : "Add"}
        isOpen={isOpen}
        onClose={onClose}
        onAdd={handleSubmit(onAdd)}
        onUpdate={handleSubmit(onUpdate)}
        isSubmitting={isSubmitting}
      >
        <FormRow label="First Name" size="sm" fieldError={errors.FirstName}>
          <InputWrapper
            name="FirstName"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder=""
            maskInputField={isTextMasked}
          />
        </FormRow>
        <FormRow label="Last Name" size="sm" fieldError={errors.LastName}>
          <InputWrapper
            name="LastName"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder=""
            maskInputField={isTextMasked}
          />
        </FormRow>
      </HistoryModal>
    </HistorySection>
  );
};

export default ChildrenHistoryPage;
